import { EllipsoidTerrainProvider, Ion, IonWorldImageryStyle, UrlTemplateImageryProvider, createGooglePhotorealistic3DTileset, createOsmBuildingsAsync, createWorldImageryAsync, createWorldTerrainAsync } from 'cesium';
import { apply } from 'ol-mapbox-style';
import { platformModifierKey } from 'ol/events/condition';
import "ol/ol.css";
import { fromLonLat, toLonLat } from "ol/proj";
import { OSM } from 'ol/source';
import OLCesium from 'olcs/OLCesium';
import OverlaySynchronizer from 'olcs/OverlaySynchronizer';
import RasterSynchronizer from 'olcs/RasterSynchronizer';
import VectorSynchronizer from 'olcs/VectorSynchronizer';
import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Dropdown as RBDropdown, Spinner } from "react-bootstrap";
import { useTranslation } from 'react-i18next';
import { RControl, RLayerTile, RMap, useOL } from "rlayers";
import Button from "../common/components/Button";
import { DefaultButton } from '../common/components/DefaultButton';
import Dropdown from "../common/components/Dropdown";
import { FloatingLabel } from "../common/components/FloatingLabel";
import { Col, Row } from "../common/components/Grid";
import { useResizeContext } from '../common/components/ResizeablePanel';
import { useBroadcast, useSubscribe } from '../common/hooks/broadcastHook';
import useClientConfig from "../common/hooks/clientConfigHook";
import useNetwork from '../common/hooks/networkHook';
import { ConfirmDialog, ReactNodeDialog, ShowMessageDialog } from '../common/utils/dialogUtlities';
import { safeHtml } from '../common/utils/safeHtml';
import useCredentials from "../common/hooks/credentialsHook";
import { canInsertPath } from '../reducers/mapPathSlice';
import { canUpdateMapView, updateMapView, useMapView } from '../reducers/mapViewSlice';
import { canInsertZone } from '../reducers/mapZoneSlice';
import { altCoord } from '../utils/geoUtilities';
import { RGeoSearch } from './map/GeoSearch';
import { LayerManager } from './map/LayerManager';
import { ActiveJobLayer, CrewLayer, LocationLayer } from './map/Marker';
import { PathLayers } from "./map/Path";
import { PathEditor } from './map/PathEditor';
import { Overlays } from "./map/TileLayer";
import { ZoneLayers } from "./map/Zone";
import { ZoneEditor } from './map/ZoneEditor';
import { RKeyboardPan, RKeyboardZoom } from 'rlayers/interaction';
import { RMeasure } from './map/Distance';
import { makeTileGrid } from '../utils/mapUtilities';
import { FormDialog } from '../common/components/CustomForm';
import { GeoJobCreate } from './map/GeoJobCreate';
import { GeoMapContext } from './map/GeoMapContext';
import { MapHandlersProvider, useMapHandlers } from './map/MapHandlers';
import Select from '../common/components/Select';
import { GuideButton } from '../common/components/GuideButton';
import { useUpsertSetting } from '../common/hooks/settingsHooks';

class CustomRasterSynchronizer extends RasterSynchronizer {
    destroyCesiumObject(object) {
        if (!object.isDestroyed()) {
            object.destroy();
        }
    }
}
class CustomVectorSynchronizer extends VectorSynchronizer {
    destroyCesiumObject(object) {
        if (!object.getRootPrimitive().isDestroyed()) {
            object.getRootPrimitive().destroy();
        }
    }
}

const CesiumMap = ({ visible, showBuildings, showTerrain, baseLayer }) => {
    const clientConfig = useClientConfig();
    const cesium = useRef(null);
    const buildings = useRef(false);
    const buildingRef = useRef();
    const photoLayerRef = useRef();
    const { map } = useOL();
    
    const toggleBuildings = useCallback(() => {
        if (showTerrain && showBuildings && baseLayer !== 'Photo') {
            if (buildings.current && buildingRef.current) {
                if (!cesium.current.getCesiumScene().primitives.contains(buildingRef.current)) {
                    cesium.current.getCesiumScene().primitives.add(buildingRef.current)
                }
            } else if (!buildings.current) {
                buildings.current = true;
                createOsmBuildingsAsync().then(b => {
                    buildingRef.current = b;
                    cesium.current.getCesiumScene().primitives.add(buildingRef.current);
                })
            }
        } else if (buildings.current) {
            buildings.current = false;
            if (buildingRef.current && cesium.current.getCesiumScene().primitives.contains(buildingRef.current)) {
                cesium.current.getCesiumScene().primitives.remove(buildingRef.current);
                buildingRef.current = null;
            }
        }
    }, [showTerrain, showBuildings, baseLayer]);

    const toggleTerrain = useCallback(() => {
        if (showTerrain) {
            createWorldTerrainAsync().then(terrain => {
                cesium.current.getCesiumScene().globe.terrainProvider = terrain;
                toggleBuildings();
            })
        } else {
            cesium.current.getCesiumScene().globe.terrainProvider = new EllipsoidTerrainProvider();
            toggleBuildings();
        }
    }, [showTerrain, toggleBuildings]);

    const setBackground = useCallback(async (noRemove) => {
        if (baseLayer === 'Photo') {
            if (!photoLayerRef.currnt) {
                const tileset = await createGooglePhotorealistic3DTileset();
                cesium.current.getCesiumScene().primitives.add(tileset);
                photoLayerRef.current = tileset;
                const imageryLayers = cesium.current.getCesiumScene().globe.imageryLayers;
                if (!noRemove) {
                    imageryLayers.remove(imageryLayers.get(0), true);
                }
            }
        } else {
            if (photoLayerRef.current) {
                if (cesium.current.getCesiumScene().primitives.contains(photoLayerRef.current)) {
                    cesium.current.getCesiumScene().primitives.remove(photoLayerRef.current);
                }
                photoLayerRef.current = null;
            }
            const terrain = await (baseLayer === 'Aerial' ? createWorldImageryAsync({ style: IonWorldImageryStyle.AERIAL_WITH_LABELS }) : Promise.resolve(new UrlTemplateImageryProvider({
                url: clientConfig.mapTiles.url.replace('{style}', baseLayer  + '/{z}/{x}/{y}.png'),
                maximumRequests: 2,
                maximumLevel: 19,
                request: {
                    timeout: 15000
                }
            })));
            const imageryLayers = cesium.current.getCesiumScene().globe.imageryLayers;
            if (!noRemove) {
                imageryLayers.remove(imageryLayers.get(0), true);
            }
            imageryLayers.addImageryProvider(terrain, 0);
            toggleTerrain();
        }
    }, [clientConfig, baseLayer, toggleTerrain])

    useEffect(() => {
        (async () => {
            if (visible) {
                if (!cesium.current || cesium.current.getOlMap() !== map) {
                    Ion.defaultAccessToken = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJqdGkiOiIyNjYwYzkxZi1kMDhkLTQ5NzUtYTkwNS1hYzJiYmU1MmIyYzEiLCJpZCI6MTcwNzA1LCJpYXQiOjE2OTY4MTMzMjJ9.Sg548859FEwZ9A83nE4l_x8tSQI4QaSNJB3fpTASrtY';
                    cesium.current = (new OLCesium({
                        map: map,
                        layers: map.getLayers().getArray().filter(l => !l.get('mapbox-source')),
                        createSynchronizers: (map, scene) => {
                            return [
                            new CustomRasterSynchronizer(map, scene),
                            new CustomVectorSynchronizer(map, scene),
                            new OverlaySynchronizer(map, scene)
                            ]
                        },
                        cesiumOptions: {
                            baseLayerPicker: false
                        }
                    }));
                    cesium.current.setEnabled(true);              
                    setBackground(true);
                    toggleTerrain();
                } else {
                    cesium.current.setEnabled(true);
                    toggleTerrain();
                }
            } else if (cesium.current) {
                cesium.current.setEnabled(false);
            }
        })();
    }, [map, visible, toggleTerrain, setBackground]);

    useEffect(() => {
        if (cesium.current) {
            setBackground();
        }
    }, [clientConfig, baseLayer, setBackground])

    useEffect(() => {
        if (cesium.current) {
            toggleTerrain();
        }
    }, [showTerrain, showBuildings, toggleTerrain])

    return null;
}

const MapboxStyle = ({ url }) => {
    const {map} = useOL();
    
    useEffect(() => {
        if (map && url) {
            const layers = map.getLayers().getArray();
            for (let i = layers.length - 1; i >= 0; --i) {
                if (layers[i].get('mapbox-source')) {
                    map.removeLayer(layers[i]);
                }
            }
            apply(map, url)
            .catch(error => {
                console.error('Error applying Mapbox style:', error);
            })
        }
    }, [map, url])

    return null;
}

const AquireLocation = ({ acquire, onAcquire }) => {
    const { t } = useTranslation();
    const network = useNetwork();
    const { map } = useOL();
    const clientConfig = useClientConfig();
    const { register } = useMapHandlers();
    const [def, setDef] = useState([]);
    const [info, setInfo] = useState({});
    const [showInfo, setShowInfo] = useState(false);
    const [pointQueryResults, setPointQueryResults] = useState([]);
    const [selectedPointQuery, setSelectedPointQuery] = useState('');

    const [location, setLocation] = useState();
    const [altCoordAddr, setAltCoordAddr] = useState();

    const clearPointInfo = useCallback(() => {
        setDef([]);
        setInfo({});
    }, []);

    const closePointInfoDialog = useCallback(() => {
        clearPointInfo();
        setPointQueryResults([]);
        setSelectedPointQuery('');
        setShowInfo(false);
    }, [clearPointInfo]);

    const pointQueryOptions = useMemo(() => {
        return pointQueryResults.map((result, index) => ({ label: result.label, value: String(index) }));
    }, [pointQueryResults]);

    const handlePointQuerySelect = useCallback((value) => {
        setSelectedPointQuery(value);
        if (value === '' || value === undefined || value === null) {
            clearPointInfo();
            return;
        }
        const index = Number(value);
        const result = pointQueryResults[index];
        if (!result) {
            clearPointInfo();
            return;
        }
        setDef(result.form);
        setInfo(result.info);
    }, [pointQueryResults, clearPointInfo]);

    const pointQueryHeaderControls = useMemo(() => {
        if (pointQueryResults.length <= 1) {
            return null;
        }
        return <Select placeholder={t('locationInformationOnMap')} hideLabel={true} value={selectedPointQuery} onChange={handlePointQuerySelect} options={pointQueryOptions} labelField="label" valueField="value"></Select>;
    }, [pointQueryResults.length, selectedPointQuery, handlePointQuerySelect, pointQueryOptions, t]);

    const handleMapClick = useCallback(async (e) => {
        if (!map) {
            return false;
        }
        if (acquire || (platformModifierKey(e) && e.originalEvent.shiftKey)) {
            const coords = e.map.getCoordinateFromPixel(e.pixel);
            const [longitude, latitude] = toLonLat(coords);
            if (acquire) {
                setLocation({ longitude, latitude });
                altCoord(network, latitude, longitude).then(data => setAltCoordAddr(data.addr));
                onAcquire({ longitude, latitude });
            } else {
                navigator.clipboard.writeText(latitude + ',' + longitude);
            }
            return true;
        }
        const pointQueries = clientConfig.mapTiles?.pointQueries ?? [];
        if (pointQueries.length === 0) {
            return false;
        }
        const coords = e.map.getCoordinateFromPixel(e.pixel);
        const tasks = pointQueries.map(async (q, index) => {
            if (!q) {
                return null;
            }
            if (q.layer && !map.getAllLayers().find(l => l.get('title') === q.layer)?.isVisible()) {
                return null;
            }
            try {
                const response = await fetch(q.url.replace('{{x}}', coords[0]).replace('{{y}}', coords[1]), { method: 'GET', mode: 'cors' });
                const data = await response.json();
                let result;
                try {
                    result = eval(q?.selector);
                } catch (error) {
                    console.error('Point query selector error:', error);
                }
                if (!result) {
                    return null;
                }
                return { label: q.layer ?? q.name ?? t('pointQuery_one', { index: index + 1 }), form: q.form, info: result, data };
            } catch (error) {
                console.error('Point query request failed:', error);
                return null;
            }
        });
        const results = (await Promise.all(tasks)).filter(Boolean);
        if (results.length === 0) {
            return false;
        }
        if (results.length > 1) {
            clearPointInfo();
            setPointQueryResults(results);
            setSelectedPointQuery('0');
            setDef(results[0].form);
            setInfo(results[0].info);
            setShowInfo(true);
            return true;
        }
        setDef(results[0].form);
        setInfo(results[0].info);
        setShowInfo(true);
        return true;
    }, [t, map, acquire, network, onAcquire, clientConfig.mapTiles?.pointQueries, clearPointInfo]);

    useEffect(() => register(handleMapClick), [register, handleMapClick]);

    useEffect(() => {
        if (!map) {
            return undefined;
        }
        map.getTargetElement().style.cursor = acquire ? 'crosshair' : 'auto';
        return () => {
            map.getTargetElement().style.cursor = 'auto';
        }
    }, [acquire, map]);

    if (location) {
        return <ReactNodeDialog title={t('location', { count: 1 })} showModal={true} onClose={() => { setLocation(); setAltCoordAddr(); }}>
            <Row>
                <Col>{t('latitude')} &amp; {t('longitude')}:</Col>
                <Col>{location.latitude}, {location.longitude}</Col>
                <Col xs="auto">
                    <DefaultButton kind="copy" onClick={() => navigator.clipboard.writeText(location.latitude + ',' + location.longitude)}></DefaultButton>
                </Col>
            </Row>
            {
                clientConfig.altcoordMode !== 'none' && altCoordAddr ? <Row>
                    <Col>{clientConfig.altcoordLbl}</Col>
                    <Col>{altCoordAddr}</Col>
                    <Col xs="auto">
                        <DefaultButton kind="copy" onClick={() => navigator.clipboard.writeText(altCoordAddr)}></DefaultButton>
                    </Col>
                </Row> : null
            }
        </ReactNodeDialog>
    }

    return <>
        <FormDialog headerControls={pointQueryHeaderControls} def={def} value={info} showModal={showInfo} title={t('locationInformationOnMap')} disabled={true} onClose={closePointInfoDialog}></FormDialog>
    </>
}

const GeoMapContorlOverlay = ({showLabel, setShowLabel, allowEdit, geocontrols, enable3D, guide }) => {
    const { t } = useTranslation();
    const clientConfig = useClientConfig();
    const credentials = useCredentials();
    const network = useNetwork();
    const upsertSetting = useUpsertSetting();
    const [cesiumVisible, setCesiumVisible] = useState(false);
    const [cesiumTerrain, setCesiumTerrain] = useState(true);
    const [cesiumBuildings, setCesiumBuildings] = useState(true);
    const [cesiumMapStyle, setCesiumMapStyle] = useState('en');
    const [acquireLocation, setAcquireLocation] = useState(false);
    const [editZones, setEditZones] = useState(false);
    const [editPaths, setEditPaths] = useState(false);
    const [showHelp, setShowHelp] = useState(false);
    const [pendingViewUpdate, setPendingViewUpdate] = useState();
    const { map } = useOL();
    

    const helpText = `<p>To navigate on the map:</p>
        <ul>
            <li>Zoom: + / -, scroll wheel, double-click, Shift + click &amp; drag</li>
            <li>Pan: &larr; / &rarr;, click &amp; drag</li>
            <li>Rotate: Alt + Shift + click &amp; drag</li>
            <li>Get Coordinates: Ctrl / &#8984 + Shift + click</li>
            <li>3D camera angle: Alt + click &amp; drag</li>
        </ul>
        <p>To move a map pin hold Ctrl / &#8984; while clicking and dragging the marker</p>
        <p>When drawing hold Shift to draw freehand</p>
        <p>Photorealistic 3D tiles will cover custom overlays but still show markers, paths and zones</p>
    `;

    const arcgisStyles = [
        { id: 'navigation', name: t('navigation'), url: 'basemapstyles/arcgis/rest/services/styles/v2/styles/osm/navigation' },
        { id: 'navigationDetailed', name: t('navigationDetailed'), url: 'basemapstyles/arcgis/rest/services/styles/v2/styles/osm/standard' },
        { id: 'navigationTerrain', name: t('navigationTerrain'), url: 'basemapstyles/arcgis/rest/services/styles/v2/styles/osm/standard-relief' },
        { id: 'satelliteImagery', name: t('satelliteImagery'), url: 'basemapstyles/arcgis/rest/services/styles/v2/styles/osm/hybrid' },
        { id: 'navigationNight', name: t('navigationNight'), url: 'basemapstyles/arcgis/rest/services/styles/v2/styles/osm/navigation-dark' },
        { id: 'nova', name: 'Nova', url: 'basemapstyles/arcgis/rest/services/styles/v2/styles/arcgis/nova' },
        { id: 'greyLight', name: t('greyLight'), url: 'basemapstyles/arcgis/rest/services/styles/v2/styles/osm/light-gray' },
        { id: 'greyDark', name: t('greyDark'), url: 'basemapstyles/arcgis/rest/services/styles/v2/styles/osm/dark-gray' }
    ]
    const [mapStyle, setMapStyle] = useState(() => {
        let style = arcgisStyles[0];
        if (clientConfig.mapTiles?.defaultBase) {
            style = arcgisStyles.find(s => s.id === clientConfig.mapTiles.defaultBase) ?? style;
        }
        return style.url;
    });
    
    const base = useMemo(() => {
        if (!clientConfig?.mapTiles?.mode || clientConfig?.mapTiles.mode === 'none') {
            return null;
        } else if (clientConfig.mapTiles?.mode === 'base') {   
            return <RLayerTile tileGrid={makeTileGrid(3, 18)} url={clientConfig.mapTiles.url} opacity={1} crossOrigin="anonymous" visible={true} attributions={OSM.ATTRIBUTION}></RLayerTile>
        } else {
            return <MapboxStyle url={clientConfig.mapTiles.url.replace('{style}', mapStyle)}></MapboxStyle>
        }
    }, [clientConfig, mapStyle]);

    const value = useMemo(() => ({ cesiumVisible }), [cesiumVisible]);

    if (!clientConfig?.mapTiles?.mode || clientConfig?.mapTiles.mode === 'none') {
        return null;
    }

    return <div id="geoMap"><GeoMapContext.Provider value={value}><div style={{position: 'absolute', top: 0, left: 0, marginTop: '12px', marginLeft: '36px', width: 'calc(100% - 112px)', zIndex: 5}}>
            <ConfirmDialog showModal={!!pendingViewUpdate} title={t('chooseMapViewTitle')} message={t('chooseMapViewMsg')} okLabel={t('yes')} cancelLabel={t('no')} onClose={result => {
                if (result) {
                    updateMapView(network, pendingViewUpdate);
                } else {
                    upsertSetting(credentials.username, 'mapView', pendingViewUpdate);
                }
                setPendingViewUpdate();
            }}></ConfirmDialog>
            { base }
            { clientConfig.mapTiles.enable3D ? <CesiumMap visible={cesiumVisible} showBuildings={cesiumBuildings} showTerrain={cesiumTerrain} baseLayer={cesiumMapStyle}></CesiumMap> : null }
            <Row>
                <Col></Col>
                { enable3D && clientConfig.mapTiles.enable3D ? <Col xs="auto">
                    <FloatingLabel hideLabel={true} placeholder="">
                        <Button variant="solid-primary" onClick={() => setCesiumVisible(!cesiumVisible)}>{cesiumVisible ? t('2dimensional') : t('3dimensional')}</Button>
                    </FloatingLabel>
                </Col> : null }
                <Col xs="auto">
                    {cesiumVisible 
                        ? <FloatingLabel hideLabel={true} placeholder="">
                            <RBDropdown>
                                <RBDropdown.Toggle id="baseMap" variant="primary">
                                    {t('baseMap')}
                                </RBDropdown.Toggle>
                                <RBDropdown.Menu>
                                    <RBDropdown.Item onClick={() => setCesiumMapStyle('en')}>{t('navigation')}</RBDropdown.Item>
                                    <RBDropdown.Item onClick={() => setCesiumMapStyle('Aerial')}>{t('satelliteImagery')}</RBDropdown.Item>
                                    <RBDropdown.Item onClick={() => setCesiumMapStyle('Photo')}>{t('photoTiles')}</RBDropdown.Item>
                                    <RBDropdown.Item disabled={!cesiumTerrain || cesiumMapStyle === 'Photo'} onClick={() => setCesiumBuildings(!cesiumBuildings)}>{cesiumBuildings ? t('hide') : t('show')} {t('buildings')}</RBDropdown.Item>
                                    <RBDropdown.Item disabled={cesiumMapStyle === 'Photo'} onClick={() => setCesiumTerrain(!cesiumTerrain)}>{cesiumTerrain ? t('hide') : t('show')} {t('terrain')}</RBDropdown.Item>
                                </RBDropdown.Menu>
                            </RBDropdown>
                        </FloatingLabel>
                        : <Dropdown className='' variant="primary" hideLabel={true} disabled={clientConfig.mapTiles?.mode === 'base'} placeholder={arcgisStyles.find(s => s.url === mapStyle)?.name} valueField="url" labelField="name" options={arcgisStyles.filter(s => s.url !== mapStyle)} onChange={setMapStyle}></Dropdown>}
                </Col>
                <Col xs="auto">
                    <FloatingLabel hideLabel={true} placeholder=''>
                        <RBDropdown>
                            <RBDropdown.Toggle id="geoMapTools" variant="primary">
                                Tools
                            </RBDropdown.Toggle>
                            <RBDropdown.Menu align="right">
                                { allowEdit && canInsertZone(credentials, null) ? <RBDropdown.Item onClick={() => setEditZones(true)}>{t('editZones')}</RBDropdown.Item> : null }
                                { allowEdit && canInsertPath(credentials, null) ? <RBDropdown.Item onClick={() => setEditPaths(true)}>{t('editPaths')}</RBDropdown.Item> : null }
                                { allowEdit && <RBDropdown.Item onClick={() => {
                                    const [ longitude, latitude ] = toLonLat(map.getView().getCenter());
                                    const zoom = map.getView().getZoom();
                                    const rotation = map.getView().getRotation();
                                    const view = { longitude, latitude, zoom, rotation };
                                    if (canUpdateMapView(credentials)) {
                                        setPendingViewUpdate(view);
                                    } else {
                                        upsertSetting(credentials.username, 'mapView', view);
                                    }
                                }}>{t('setDefaultView')}</RBDropdown.Item> }
                                <RBDropdown.Item onClick={() => setAcquireLocation(true)}>{t('getCoordinates')}</RBDropdown.Item>
                                <RBDropdown.Item onClick={() => setShowLabel(!showLabel)}>{ showLabel ? t('hide') : t('show') } {t('label')}</RBDropdown.Item>
                                <RBDropdown.Item onClick={() => setShowHelp(true)}>{t('help')}</RBDropdown.Item>
                            </RBDropdown.Menu>
                        </RBDropdown>
                    </FloatingLabel>
                </Col>
            </Row>
        </div>
        <GuideButton guide={guide ?? 78165} variant="solid-primary" hideLabel={true} style={{ position: 'absolute', left: '30px', top: '32px', width: '38px', zIndex: 80 }}></GuideButton>
        <AquireLocation acquire={acquireLocation} onAcquire={() => setAcquireLocation(false)}></AquireLocation>
        { cesiumVisible ? null : <RMeasure style={{ position: 'absolute', left: '30px', top: '112px' }}></RMeasure> }
        { canInsertZone(credentials, null) ? <ZoneEditor show={editZones} onClose={() => setEditZones(false)}></ZoneEditor> : null }
        { canInsertPath(credentials, null) ? <PathEditor show={editPaths} onClose={() => setEditPaths(false)}></PathEditor> : null }
        { showHelp ? ShowMessageDialog({ showModal: true, title: t('help'), message: safeHtml(helpText), onClose: () => setShowHelp(false)}) : null }
        { geocontrols }
        </GeoMapContext.Provider></div>
}

export const DefaultView = ({ latitude, longitude }) => {
    const clientConfig = useClientConfig();
    const { map } = useOL();
    const {width, height} = useResizeContext();
    const view = useMapView();

    useEffect(() => {
        map?.updateSize();
    }, [map, width, height])

    useEffect(() => {
        if (map) {
            map.getView().setCenter(fromLonLat([longitude ?? view.longitude, latitude ?? view.latitude]));
            map.updateSize();
        }
    }, [map, view, longitude, latitude])

    useEffect(() => {
        if (map) {
            map.getView().setZoom(view.zoom);
            if (view.rotation !== undefined && view.rotation !== null) {
                map.getView().setRotation(view.rotation)
            }
            map.updateSize();
        }
    }, [map, view])

    useSubscribe('locate', obj => {
        map.getView().setCenter(fromLonLat([ obj.longitude, obj.latitude ]));
        if (clientConfig.mapTiles?.locateZoom && map.getView().getZoom() < clientConfig.mapTiles?.locateZoom) {
            map.getView().setZoom(clientConfig.mapTiles?.locateZoom);
        }
    }, [map, clientConfig.mapTiles?.locateZoom]);

    return null;
}

export const BaseMap = ({ controls, geocontrols, layers, allowEdit, onJobCreate, defaultFocus, guide, enable3D = true }) => {
    const [showLabel, setShowLabel] = useState(true);
    const clientConfig = useClientConfig();

    const refCallback = useCallback(ref => {
        if (ref?.target?.current) {
            ref.target.current.tabIndex = 0;
            if (defaultFocus) {
                ref.target.current.focus();
            }
            ref.target.current.addEventListener('mouseenter', () => ref.target.current.focus());
        }
    }, []);

    const layerManager = useMemo(() => <LayerManager>
        <ZoneLayers mode='ol' zIndex={50}></ZoneLayers>
        <PathLayers mode='ol' zIndex={60}></PathLayers>
        <Overlays mode='ol' zIndex={40}></Overlays>
        {layers?.(showLabel)}
    </LayerManager>, [layers, showLabel])

    return <div className="w-100 h-100" style={{position: 'relative'}}>
        <RMap ref={refCallback} initial={{center: fromLonLat([-0.09, 51.505]), zoom: 13 }} className="w-100 h-100" style={{ position: 'relative', top: 0, left: 0 }} noDefaultControls={true}>
            <MapHandlersProvider>
                <RControl.RZoom></RControl.RZoom>
                <RControl.RRotate autoHide={false}></RControl.RRotate>
                <RKeyboardPan></RKeyboardPan>
                <RKeyboardZoom></RKeyboardZoom>
                <RControl.RAttribution collapsed={clientConfig.mapTiles?.mode === 'base'} collapsible={clientConfig.mapTiles?.mode === 'base'}></RControl.RAttribution>
                <RGeoSearch style={{ position: 'absolute', left: '30px', top: '72px' }} onJobCreate={onJobCreate}></RGeoSearch>
                <GeoMapContorlOverlay showLabel={showLabel} setShowLabel={setShowLabel} allowEdit={allowEdit} geocontrols={geocontrols} enable3D={enable3D} guide={guide}></GeoMapContorlOverlay>
                { controls }
                {layerManager}
            </MapHandlersProvider>
        </RMap>
    </div>
}

const GeoMap = ({ latitude, longitude }) => {
    const clientConfig = useClientConfig();
    const broadcast = useBroadcast();

    const layers = useCallback((showLabel) => <>
        { !clientConfig.trackerOnly && <ActiveJobLayer mode='ol' showLabel={showLabel} ></ActiveJobLayer> }
        <CrewLayer mode='ol' showLabel={showLabel}></CrewLayer>
        <LocationLayer mode='ol' showLabel={showLabel} ></LocationLayer>
    </>, [clientConfig.trackerOnly]);

    const onJobCreate = (locationInfo) => {
        broadcast('createJob', locationInfo);
    }

    const createJobCtrl = !clientConfig.trackerOnly && <GeoJobCreate style={{ position: 'absolute', left: '30px', top: '190px' }}></GeoJobCreate>;

    return <Suspense fallback={<Spinner animation="border" variant="primary"></Spinner>}>
        <BaseMap controls={<DefaultView latitude={latitude} longitude={longitude}></DefaultView>} geocontrols={createJobCtrl} layers={layers} allowEdit={true} onJobCreate={onJobCreate} defaultFocus={true}>
        </BaseMap>
    </Suspense>
}

export default GeoMap;
